import React, { useContext, useState } from 'react'
import { Box, Button, TextField } from '@mui/material'
import FixedWidthContainer from 'components/FixedWidthContainer'
import {
  formatPhoneNumber,
  validatePhoneNumber,
  validateEmail
} from 'utils/formatUtils'
import DealerSignupSection from 'components/DealerSignupSection'
import { SnackbarContext } from 'contexts/SnackbarContext'
import sendContactUs from 'api/contactUs'
import SectionHeader from 'components/SectionHeader'
import NameFields from 'components/NameFields'
import { useTranslation } from 'react-i18next'

const ContactUsPage = () => {
  const { showMessage } = useContext(SnackbarContext)
  const { t } = useTranslation()

  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    comments: ''
  })

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    comments: ''
  })

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'phoneNumber') {
      formattedValue = formatPhoneNumber(value)
    }

    setFormValues({
      ...formValues,
      [name]: formattedValue
    })
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let errorMessage = ''

    switch (name) {
      case 'firstName':
        if (value.trim() === '')
          errorMessage = t('contactUs.errors.firstNameRequired')
        break
      case 'lastName':
        if (value.trim() === '')
          errorMessage = t('contactUs.errors.lastNameRequired')
        break
      case 'phoneNumber':
        if (value.trim() === '') {
          errorMessage = t('contactUs.errors.phoneNumberRequired')
        } else if (!validatePhoneNumber(value)) {
          errorMessage = t('contactUs.errors.invalidPhoneNumber')
        }
        break
      case 'email':
        if (value.trim() === '') {
          errorMessage = t('contactUs.errors.emailRequired')
        } else if (!validateEmail(value)) {
          errorMessage = t('contactUs.errors.invalidEmail')
        }
        break
      case 'comments':
        if (value.trim() === '') {
          errorMessage = t('contactUs.errors.commentsRequired')
        }
        break
    }

    setErrorMessages(prev => ({ ...prev, [name]: errorMessage }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: Record<string, string> = {
      firstName: formValues.firstName.trim()
        ? ''
        : t('contactUs.errors.firstNameRequired'),
      lastName: formValues.lastName.trim()
        ? ''
        : t('contactUs.errors.lastNameRequired'),
      phoneNumber: formValues.phoneNumber.trim()
        ? validatePhoneNumber(formValues.phoneNumber)
          ? ''
          : t('contactUs.errors.invalidPhoneNumber')
        : t('contactUs.errors.phoneNumberRequired'),
      email: formValues.email.trim()
        ? validateEmail(formValues.email)
          ? ''
          : t('contactUs.errors.invalidEmail')
        : t('contactUs.errors.emailRequired'),
      comments: formValues.comments.trim()
        ? ''
        : t('contactUs.errors.commentsRequired')
    }

    setErrorMessages(newErrors)

    const hasErrors = Object.values(newErrors).some(error => error !== '')
    if (hasErrors) return

    const result = await sendContactUs(formValues)

    if (result.success) {
      showMessage(t('contactUs.successMessage'))
      setFormValues({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        comments: ''
      })
      setErrorMessages({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        comments: ''
      })
    } else {
      showMessage(result.error, 'error')
    }
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '80vh' }}>
      <FixedWidthContainer>
        <SectionHeader title={t('contactUs.title')} />

        <Box component='form' noValidate onSubmit={handleSubmit}>
          <NameFields
            formValues={{
              firstName: formValues.firstName,
              lastName: formValues.lastName
            }}
            errors={{
              firstName: errorMessages.firstName,
              lastName: errorMessages.lastName
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            size='medium'
          />

          <TextField
            label={t('contactUs.phoneNumber')}
            name='phoneNumber'
            value={formValues.phoneNumber}
            onChange={handleInputChange}
            onBlur={handleBlur}
            fullWidth
            margin='dense'
            variant='outlined'
            required
            error={Boolean(errorMessages.phoneNumber)}
            helperText={errorMessages.phoneNumber}
            inputProps={{ maxLength: 12 }}
          />

          <TextField
            label={t('contactUs.email')}
            name='email'
            type='email'
            value={formValues.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            fullWidth
            margin='dense'
            variant='outlined'
            required
            error={Boolean(errorMessages.email)}
            helperText={errorMessages.email}
            inputProps={{ maxLength: 50 }}
          />

          <TextField
            label={t('contactUs.comments')}
            name='comments'
            value={formValues.comments}
            onChange={handleInputChange}
            onBlur={handleBlur}
            fullWidth
            margin='dense'
            variant='outlined'
            required
            error={Boolean(errorMessages.comments)}
            helperText={errorMessages.comments}
            multiline
            rows={4}
            inputProps={{ maxLength: 500 }}
          />

          <Box display='flex' justifyContent='center' sx={{ mt: 3 }}>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              sx={{
                width: '120px',
                height: '42px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              {t('contactUs.submit')}
            </Button>
          </Box>
        </Box>
      </FixedWidthContainer>

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%'
        }}
      >
        <DealerSignupSection />
      </Box>
    </Box>
  )
}

export default ContactUsPage
